import { defineComponent as _defineComponent } from 'vue'
import { dispatchGetRetentionCount } from '@/store/dispatchers/uiDispatchers';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import store from '@/store';
import { HOME } from '@/routerNames';
import apiService from '@/services/ApiService';
import notificationService from '@/services/NotificationService';
import authService from '@/services/AuthService';
import { Company } from '@/models/Company';
import CompanyLogoInput from '@/components/layout/inputs/CompanyLogoInput/CompanyLogoInput.vue';
import { Loading } from 'element-ui';
import { commitSetCompanyLogo } from '@/store/commits/companyCommits';
import { commitSetCompanyRetention } from '@/store/commits/companyCommits';
import { Logger } from '@/other/Logger';


export default /*#__PURE__*/_defineComponent({
  __name: 'CompanyPage',
  setup(__props) {

const DEFAULT_SOFT_DELETE_DAYS = 160;
const DEFAULT_HARD_DELETE_DAYS = 30;

const log = new Logger('CompanyPage');

const router = useRouter();

const loader = ref<any>(null);
const company = ref<Company | null>(null);
const companyFormData = ref({
  logo: null as File | null,
  isDeleted: false,
});
const retentionErrors = ref({
  softDeleteDays: false,
  hardDeleteDays: false,
});

const companyLogo = computed(() => store.state.company.logo);

const softDeleteDaysError = computed(() => {
  if (!company.value || !company.value.retention) return '';
  const value = Number(company.value.retention.softDeleteDays);
  if (!value || isNaN(value)) {
    return `Must be a correct number`;
  }
  return '';
});

const hardDeleteDaysError = computed(() => {
  if (!company.value || !company.value.retention) return '';
  const value = Number(company.value.retention.hardDeleteDays);
  if (!value || isNaN(value)) {
    return `Must be a correct number`;
  }
  return '';
});

const hasRetentionErrors = computed(() => {
  return !!softDeleteDaysError.value || !!hardDeleteDaysError.value;
});

const handleSoftDeleteChange = () => {
  if (!company.value || !company.value.retention) return;

  const value = Number(company.value.retention.softDeleteDays);

  retentionErrors.value.softDeleteDays = isNaN(value) || value < 1;

  if (value < 1) {
    company.value.retention.softDeleteDays = 1;
  }
};

const handleHardDeleteChange = () => {
  if (!company.value || !company.value.retention) return;

  const value = Number(company.value.retention.hardDeleteDays);

  retentionErrors.value.hardDeleteDays = isNaN(value) || value < 1;

  if (value < 1) {
    company.value.retention.hardDeleteDays = 1;
  }
};

const hasTransparentPixels = (imageData: ImageData): boolean => {
  const data = imageData.data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) {
      return true;
    }
  }
  return false;
};

const compressImage = (file: File, maxSizeKB: number): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        log.info(`Original image size: ${Math.round(file.size / 1024)}KB`);

        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        const MAX_WIDTH = 1200;
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        const imageData = ctx.getImageData(0, 0, width, height);
        const hasTransparency = hasTransparentPixels(imageData);

        const tryCompress = () => {
          let quality = 0.9;
          let compressedFile: File | null = null;

          while (quality >= 0.1) {
            const mimeType = hasTransparency ? 'image/png' : 'image/jpeg';
            const dataUrl = canvas.toDataURL(mimeType, quality);
            const base64 = dataUrl.split(',')[1];
            const byteSize = Math.round((base64.length * 3) / 4);

            const byteCharacters = atob(base64);
            const byteArrays = [];
            for (let i = 0; i < byteCharacters.length; i++) {
              byteArrays.push(byteCharacters.charCodeAt(i));
            }
            const blob = new Blob([new Uint8Array(byteArrays)], { type: mimeType });

            compressedFile = new File([blob], file.name, {
              type: mimeType,
              lastModified: Date.now(),
            });

            if (byteSize <= maxSizeKB * 1024) {
              break;
            }

            quality -= 0.1;
          }

          if (compressedFile) {
            log.info(`Compressed image size: ${Math.round(compressedFile.size / 1024)}KB`);
            resolve(compressedFile);
          } else {
            reject(new Error('Could not compress image to target size'));
          }
        };

        tryCompress();
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const setCompanyLogo = async (photo: File): Promise<void> => {
  if (!photo) return;

  if (!photo.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  try {
    const compressedPhoto = await compressImage(photo, 50);
    companyFormData.value.logo = compressedPhoto;
    companyFormData.value.isDeleted = false;
    log.info(`Compressed image size: ${Math.round(compressedPhoto.size / 1024)}KB`);
  } catch (error) {
    log.info('Error compressing image');
    throw error;
  }
};

const deleteCompanyLogo = () => {
  companyFormData.value.logo = null;
  companyFormData.value.isDeleted = true;
};

const getNewLogoUrl = async (logo: File): Promise<{ url: string; signedUrl: string }> => {
  try {
    const response = await apiService.image.upload(logo);
    return response.data;
  } catch (error) {
    log.error('Error uploading new logo');
    throw error;
  }
};

const save = async () => {
  if (hasRetentionErrors.value) {
    notificationService.error('Please ensure you entered everything correctly before saving');
    return;
  }

  loader.value = loader.value = Loading.service({ lock: true });
  const { logo, isDeleted: isLogoDeleted } = companyFormData.value;
  let signedUrl: string | null = null;
  let newURL: string | null = null;

  if (!isLogoDeleted && logo) {
    const logoData = await getNewLogoUrl(logo);
    signedUrl = logoData.signedUrl;
    newURL = logoData.url;
  }

  const resLogo = !isLogoDeleted ? (!logo ? companyLogo.value : newURL) : null;
  const companyUpdateData = !logo ? { isLogoDeleted } : { logo: newURL };

  return apiService
    .updateCompany({ ...companyUpdateData, retention: company.value!.retention })
    .then(() => {
      commitSetCompanyLogo(!signedUrl ? resLogo : signedUrl);
      commitSetCompanyRetention(company.value!.retention);

      if (company.value?.retention?.enabled) {
        dispatchGetRetentionCount();
      }

      notificationService.success('Company successfully updated');
    })
    .catch((res: any) => apiService.handleResponseError(res))
    .finally(() => {
      loader.value?.close();
      loader.value = null;
    });
};

const resetCompanySettings = async () => {
  companyFormData.value.logo = null;
  companyFormData.value.isDeleted = true;
  company.value!.retention = {
    ...company.value!.retention,
    enabled: false,
    softDeleteDays: DEFAULT_SOFT_DELETE_DAYS,
    hardDeleteDays: DEFAULT_HARD_DELETE_DAYS,
  };
  await save();
};

const load = async () => {
  loader.value = Loading.service({ lock: true });

  try {
    const isUserActive = await authService.isUserActive();
    if (!isUserActive) {
      await router.push({ name: HOME });
      return;
    }

    const response = await apiService.company.get();
    company.value = Company.parse(response.data.company);
  } catch (e) {
    await apiService.handleResponseError(e);
  } finally {
    loader.value?.close();
    loader.value = null;
  }
};

onMounted(async () => {
  await load();
});

return { __sfc: true,DEFAULT_SOFT_DELETE_DAYS, DEFAULT_HARD_DELETE_DAYS, log, router, loader, company, companyFormData, retentionErrors, companyLogo, softDeleteDaysError, hardDeleteDaysError, hasRetentionErrors, handleSoftDeleteChange, handleHardDeleteChange, hasTransparentPixels, compressImage, setCompanyLogo, deleteCompanyLogo, getNewLogoUrl, save, resetCompanySettings, load, CompanyLogoInput }
}

})